import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, Clock, Wrench, X } from "lucide-react";
import {
  SkeletonCard,
  buttonClasses,
  useIsMobile,
  useToast,
} from "../../../components/ui";
import {
  BILLING_SETTINGS_PATH,
  ROUTES,
  billingCheckoutPath,
} from "../../../routes/paths";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { markWayInFinished } from "../../auth/services/signupArrival";
import { formatCreatedOn } from "../../brand/planFormat";
import GreenCheck from "../components/GreenCheck";
import PinnedCtaBar from "../components/PinnedCtaBar";
import TransactionDetails, {
  TransactionRow,
  TransactionTone,
} from "../components/TransactionDetails";
import { CheckoutOutcome, clearResult, readResult } from "../checkoutOutcome";
import { PRODUCT_NAME } from "../data/subscriptionPlans";
import { useBillingHistory } from "../hooks/useBillingHistory";
import { usePlans } from "../hooks/usePlans";
import {
  MONTHS_PER_CYCLE,
  formatMoney,
  monthsLabel,
  planTermLabel,
  priceBreakdown,
} from "../pricing";
import { pollPrepaidOrder } from "../services/prepaidService";
import {
  fetchInvoiceDocument,
  saveInvoicePdf,
} from "../services/subscriptionsService";
import type { BillingCycleId } from "../types/billing.types";
import type { PrepaidOrder } from "../types/prepaid.types";

/** The mark at the top, and the words under it. */
const FACE: Record<
  CheckoutOutcome["state"],
  {
    Icon: React.FC<{ className?: string; "aria-hidden"?: boolean }>;
    tint: string;
    title: string;
    tone: TransactionTone;
  }
> = {
  active: {
    Icon: Check,
    tint: "bg-brand-soft text-brand-dark",
    title: "Payment successful",
    tone: "paid",
  },
  pending: {
    Icon: Clock,
    tint: "bg-[#FFF6E5] text-[#9A6700]",
    title: "We're confirming your payment",
    tone: "pending",
  },
  failed: {
    Icon: X,
    tint: "bg-[#FDECEC] text-[#D14343]",
    title: "Payment failed",
    tone: "failed",
  },
  unavailable: {
    Icon: Wrench,
    tint: "bg-[#F1F4F6] text-ink-muted",
    title: "Checkout isn't live yet",
    tone: "pending",
  },
};

/** An ISO date as a receipt writes it, or nothing where there is none. */
const day = (iso: string | null | undefined): string | null =>
  iso ? formatCreatedOn(new Date(iso)) : null;

/**
 * How a checkout ended.
 *
 * A route of its own rather than a state of the checkout, for two reasons that
 * both come from the payment window: the gateway has to be given a
 * `callback_url` to return to for the flows that redirect, and what it returns
 * to is a fresh page load carrying no router state at all. So the browser's
 * account of the outcome is left in session storage by the checkout and picked
 * up here — see `checkoutOutcome`.
 *
 * That account is a hint and is treated as one. Where there is a subscription
 * id, this screen asks the backend what actually happened and believes that
 * instead: a mandate can succeed after the tab is closed, and the webhook
 * behind that endpoint is the only thing that knows.
 *
 * The three states are deliberately not three variations on "something went
 * wrong". A successful payment is a receipt, and is the only one that says a
 * workspace has been unlocked. A pending one is a promise with nothing to do.
 * A failed one is the only one that offers a retry, and it says what was
 * turned down and by whom, because "payment failed" alone sends people to
 * support rather than to their bank.
 */
const SubscriptionResultPage: React.FC = () => {
  const { cycle } = useParams<{ cycle: string }>();
  const getToken = useTokenGetter();
  const toast = useToast();
  // Where the way onward goes: under the receipt beside nothing, or across the
  // foot of the phone under everything — see the bar at the end of this file.
  const isMobile = useIsMobile();

  // Off the URL rather than off the price list: what was bought is settled, and
  // this screen has to be able to read the outcome of it from the first frame —
  // before any list has arrived, and even if none ever does.
  //
  // Checked against the terms this app has a slug for rather than against the
  // two it used to sell, so a half-year receipts itself the day one is sold.
  const cycleId: BillingCycleId | undefined =
    cycle && cycle in MONTHS_PER_CYCLE
      ? (cycle as BillingCycleId)
      : undefined;

  // The plan itself, for the figures. It is the fallback and not the record:
  // what was actually charged comes off the subscription below wherever there
  // is one, and this only stands in for the paths that never made it that far.
  const { plans } = usePlans();
  const plan = plans.find((candidate) => candidate.id === cycleId);

  // Read once, at mount: the browser's account of what happened, and what to
  // check it against.
  const [stashed] = useState(() => (cycleId ? readResult(cycleId) : null));
  const [outcome, setOutcome] = useState<CheckoutOutcome | null>(
    stashed?.outcome ?? null
  );
  // What the backend says the order came to, once it has been asked.
  const [order, setOrder] = useState<PrepaidOrder | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // What was invoiced, for the number on the receipt and the document behind
  // the button under it. The same read the Billing tab's history draws, so the
  // two cannot disagree about which invoice this payment raised.
  const history = useBillingHistory();

  const orderId = stashed?.subscriptionId ?? null;

  /**
   * What the backend says, which outranks what the browser reported.
   *
   * Asked for repeatedly rather than once, because settling a payment is not
   * the browser's to do: the gateway's webhook does it, or the backend's sweep,
   * or the verify call the checkout made on its way here — and whichever gets
   * there first, the answer appears on this order. Usually seconds. See
   * `pollPrepaidOrder`, which gives up after about a minute and leaves the
   * order wherever it had got to.
   *
   * A `CREATED` order is reported as pending and not as a failure. The money
   * may well have been taken and be settling by another road, and telling
   * somebody who has paid that they have not is the worst thing this screen
   * could do.
   *
   * Only a success is checked, and that asymmetry is deliberate. A browser
   * reporting a payment is making a claim the server has to confirm. A browser
   * reporting a decline is reporting what the gateway's own window told it,
   * and the order behind a declined card is left `CREATED` — payable, because
   * the modal offers another go — so asking about it would answer "still
   * confirming" for a card that was turned down, and bury the reason the
   * buyer actually needs to see.
   */
  useEffect(() => {
    if (!orderId || stashed?.outcome?.state !== "active") return;

    const controller = new AbortController();
    setConfirming(true);

    void pollPrepaidOrder(getToken, orderId, {
      onUpdate: setOrder,
      signal: controller.signal,
    })
      .then((settled) => {
        if (controller.signal.aborted) return;
        setOutcome(
          settled.status === "PAID"
            ? { state: "active" }
            : settled.status === "FAILED"
            ? {
                state: "failed",
                reason:
                  "We received your payment and our team will review it shortly.",
              }
            : settled.status === "SUPERSEDED" || settled.status === "EXPIRED"
            ? {
                state: "failed",
                reason:
                  "This checkout is no longer open, so nothing was charged.",
              }
            : {
                state: "pending",
                reason:
                  "We're still confirming the payment. We'll email you the moment it's done.",
              }
        );
      })
      .catch(() => {
        // Leave the browser's account standing. It is worse to replace a
        // "payment successful" with an error because one request failed.
      })
      .finally(() => {
        if (!controller.signal.aborted) setConfirming(false);
      });

    return () => controller.abort();
  }, [getToken, orderId, stashed]);

  /**
   * A paid plan is the other way a new practice finishes its way in — the one
   * taken by somebody who was offered the free trial and bought outright
   * instead. The dashboard is the next thing after this screen either way.
   *
   * Only on a settled success, and only for a visit that began as a sign-up:
   * an established practice buying another term reaches exactly this screen and
   * is not new. `markWayInFinished` makes that second judgement itself — see
   * `signupArrival`.
   */
  useEffect(() => {
    if (outcome?.state === "active") markWayInFinished();
  }, [outcome]);

  // Nothing to report on: the URL names a cycle that does not exist, or
  // somebody has arrived here without going through a checkout.
  if (!cycleId) return <Navigate to={BILLING_SETTINGS_PATH} replace />;
  if (!outcome) return <Navigate to={BILLING_SETTINGS_PATH} replace />;

  // What the plan would have cost, where the plan is known. `null` while the
  // price list is on its way and if it never comes — which costs this screen a
  // row or two of the receipt and none of what it is for: the heading, the
  // reference and the way onward are all about a payment that has already
  // happened, and none of them is priced.
  const price = plan
    ? priceBreakdown(plan, { discount: stashed?.discount ?? 0 })
    : null;
  const productName = plan?.productName ?? PRODUCT_NAME;
  // The term as every other screen writes it — "3 month plan" — and off the
  // URL where the price list never arrived, which is the whole reason the slug
  // and the months are one table.
  const months = plan?.months ?? MONTHS_PER_CYCLE[cycleId];
  const termLabel = planTermLabel(months);
  /**
   * What was charged, in the order of who knows best.
   *
   * The record the backend has since made of it outranks everything. Failing
   * that, what the purchase was quoted at on the way through — which is the
   * only figure here that knows what coupon was on the order. The price list is
   * the last resort and does not: it quotes this plan at full price, which is
   * right for most receipts and wrong for a discounted one.
   */
  const amount =
    order?.price.amountDue ?? stashed?.amount ?? price?.total ?? null;
  const { Icon, tint, title, tone } = FACE[outcome.state];
  const paid = outcome.state === "active";
  /**
   * What the backend made of the purchase.
   *
   * The order the poll settled on is the answer; what the checkout stashed on
   * its way here is the same answer a second early, so the page heads itself
   * correctly on its first paint rather than correcting itself once the poll
   * lands. See `StashedResult.purchaseKind`.
   */
  const purchaseKind = order?.purchaseKind ?? stashed?.purchaseKind ?? null;
  /** A longer term, which replaced the one running and started today. */
  const upgraded = purchaseKind === "UPGRADE";
  /**
   * A shorter term, which is paid for and waiting its turn.
   *
   * Nothing about it is active today — the term running now is untouched and
   * keeps the workspace open to its own last day — so a receipt reading
   * "you're on 3 months" would be wrong about the one thing it is for.
   */
  const scheduled = purchaseKind === "DOWNGRADE";
  /** The day the term that was bought actually begins. Off the order the
   *  backend wrote where it has settled, and off the dates it was quoted at
   *  until then — the two agree on a queued term, whose start is the day after
   *  the running one ends rather than the day it was paid for. */
  const startsOn = day(
    order?.term?.startsOn ?? order?.startsOn ?? stashed?.startsOn
  );
  const testMode = !!stashed?.testMode;
  const discount = stashed?.discount ?? null;

  /**
   * The invoice this payment raised: the most recent one on the workspace's
   * history.
   *
   * There is no endpoint that answers "the invoice for this subscription", and
   * the newest one is the right answer in every case this screen can be in —
   * a purchase that has just gone through is the latest thing that happened on
   * the account. It is also only ever used to *name* and fetch a document, so
   * being wrong about it on some unforeseen path costs a row of the receipt
   * rather than a figure anybody is charged.
   *
   * `null` while the history is still coming, and for a payment the backend has
   * not raised an invoice for yet — which is ordinary rather than a failure, and
   * is why the row and the button are both simply absent when it is.
   */
  const invoice = paid ? history.invoices[0] ?? null : null;

  /**
   * Fetching the invoice's PDF.
   *
   * The bytes are the backend's — the invoice number, the tax split and the
   * registered details are all its to hold, and a copy drawn in the browser
   * would be a second document for one payment. The same call the history
   * table's own download makes.
   */
  const handleDownload = async () => {
    if (!invoice) return;

    setDownloading(true);
    try {
      const pdf = await fetchInvoiceDocument(getToken, invoice.id);
      saveInvoicePdf(pdf, `naurist-${invoice.number}.pdf`);
    } catch (cause) {
      toast.error(
        "Could not download your invoice",
        cause instanceof Error ? cause.message : "Please try again."
      );
      console.error("invoice download failed:", cause);
    } finally {
      setDownloading(false);
    }
  };

  /** What the receipt says, out of what is actually known. Rows with no value
   *  are dropped by the card rather than drawn as a dash. */
  const rows: TransactionRow[] = [
    { label: "Plan", value: termLabel },
    {
      label: paid ? "Amount paid" : "Amount due",
      value: amount === null ? null : formatMoney(amount),
    },
    {
      // What came off, where anything did. Off the stash and not re-derived:
      // the price list quotes this plan at full price and has no idea what
      // coupon was on the order — see `StashedResult`.
      label: "Coupon discount",
      value: discount ? formatMoney(discount) : null,
      credit: true,
    },
    {
      // The order says nothing about how it was paid — that is on the invoice's
      // payment record rather than on the order — so this row is dropped rather
      // than guessed at. The reference below is what support will ask for.
      label: "Payment method",
      value: null,
    },
    {
      label: "Invoice",
      value: invoice?.number ?? null,
      // The copy, on the line that names it. An invoice the backend has not
      // raised yet drops this row whole — see the card, which prints no row
      // it has no value for — so there is never a download here for a
      // document that does not exist. Every invoice is downloadable from
      // Billing either way.
      download: invoice
        ? {
            label: `Download invoice ${invoice.number}`,
            onClick: handleDownload,
            busy: downloading,
          }
        : undefined,
    },
    { label: "Reference", value: stashed?.paymentId ?? null },
    {
      // Only on a term that waits its turn. Every other purchase starts the
      // day it is paid for, and a "Starts on" reading today is a row that
      // says nothing.
      label: "Starts on",
      value: paid && scheduled ? startsOn : null,
    },
    {
      // Not a renewal: a term is bought outright and nothing charges again, so
      // what this date is good for is knowing when to come back. The backend
      // emails a reminder before it.
      label: "Access until",
      value: paid
        ? day(order?.term?.endsOn) ??
          (price ? formatCreatedOn(price.renewsOn) : null)
        : null,
    },
  ];

  /**
   * The way onward: one button where the payment went through, two where it
   * did not.
   *
   * The document is no longer one of them. It used to sit beside Go to
   * dashboard as its equal, which made the end of a receipt a choice between
   * leaving and keeping a copy — and on a phone that is the row a thumb lands
   * on, so the way out of the screen had to share it. The copy belongs to the
   * invoice number that is already printed on the card above, and that is
   * where it is now: an icon on its own line. See the `download` on the
   * invoice row.
   *
   * Sized by where it is standing. Pinned across the foot of a phone it is
   * `lg` and full width, which is what every other action pinned there in
   * this app is; drawn under the receipt on a desktop it keeps the `md` it
   * always wore.
   */
  const ways = (() => {
    const size = isMobile ? "lg" : "md";
    /** A phone's bar splits its width between whatever is in it; a desktop
     *  sizes each button to its own words over a floor. */
    const span = (floor: string) => (isMobile ? "flex-1" : floor);

    if (paid) {
      return (
        <Link
          to={ROUTES.dashboard}
          className={buttonClasses({
            variant: "primary",
            size,
            className: span("sm:min-w-[200px]"),
          })}
        >
          Go to dashboard
        </Link>
      );
    }

    return (
      <>
        <Link
          to={BILLING_SETTINGS_PATH}
          className={buttonClasses({
            variant: "outline",
            size,
            className: span("sm:min-w-[180px]"),
          })}
        >
          Back to plans
        </Link>
        {outcome.state !== "pending" && (
          <Link
            to={billingCheckoutPath(cycleId)}
            // Cleared on the way out, so the checkout being reopened is a
            // fresh purchase rather than one that lands straight back on the
            // result it just left.
            onClick={() => clearResult(cycleId)}
            className={buttonClasses({
              variant: "primary",
              size,
              className: span("sm:min-w-[180px]"),
            })}
          >
            Try again
          </Link>
        )}
      </>
    );
  })();

  const detail = (): string => {
    switch (outcome.state) {
      case "active":
        if (testMode) {
          return "Razorpay accepted the payment in test mode. No subscription was created - that needs the backend - so nothing has actually been unlocked.";
        }
        // An upgrade starts the moment it settles - that is what separates it
        // from a downgrade, which waits its turn - and the line under the
        // heading is where each of them says so. Neither says anything about
        // access being unlocked: they had access an hour ago and have it
        // still.
        if (upgraded) {
          return "Your upgrade is active right away. A receipt is on its way to your email.";
        }
        if (scheduled) {
          return startsOn
            ? `Nothing changes today. Your ${monthsLabel(
                months
              )} plan starts on ${startsOn} - no action needed.`
            : `Nothing changes today. Your ${monthsLabel(
                months
              )} plan starts when your current one ends - no action needed.`;
        }
        return "Your full workspace is unlocked and ready to go.";
      case "pending":
        return outcome.reason;
      case "failed":
        return outcome.reason;
      default:
        return "We can't take payment yet, so nothing has been charged and no subscription has been set up. Nothing you entered was lost.";
    }
  };

  return (
    // `min-h-full flex flex-col`, the shape every screen in this app that ends
    // in a pinned bar wears: it keeps the page filling the window when the
    // receipt is short, which is what holds the bar on the foot of the screen
    // rather than partway up it. See `AddClientPage`, which argues it.
    <div className="min-h-full flex flex-col p-page pt-page-top">
      {/* No page title: the heading below is what this screen is, and a
          "Billing" above it would only compete with it. */}
      {/* The confirmation sits lower on a phone than the 24px every other
          screen starts at. It is the whole of the page rather than the first
          thing on one, and the design gives it a clear band under the logo
          before the mark - which is also what stops the tick landing under
          the shell's own top bar on a short screen. */}
      <div className="mx-auto mt-6 flex w-full max-w-[560px] flex-1 flex-col items-center text-center max-md:mt-12 3xl:mt-10">
        {/* A payment that went through gets the mark the trial confirmation
            already uses: a green disc with the tick drawn into it and a
            ripple out of it, rather than a static glyph in a tinted circle.
            The two screens are the same moment - something worked, here is
            what you now have - and they should not say it in two different
            hands. See `GreenCheck`, which falls back to a plain tick for a
            reader who has asked for less movement or a player that will not
            load.

            Everything else keeps the glyph: a clock, a cross and a spanner
            are each saying something the tick is not, and none of them is a
            celebration. */}
        {paid ? (
          <GreenCheck size={72} />
        ) : (
          <span
            className={`flex h-16 w-16 3xl:h-[72px] 3xl:w-[72px] items-center justify-center rounded-full ${tint}`}
          >
            <Icon aria-hidden className="h-7 w-7 3xl:h-8 3xl:w-8" />
          </span>
        )}

        {/* Larger on a phone than off it, which is the other way round from
            most of this app. There the heading is competing with a page title,
            a tab bar and whatever is beside it; here it is alone on a screen
            with nothing above it but the logo, and the design has it carry the
            whole top third. The brand's deepest teal rather than plain ink,
            the shade the plans screen gives its own heading. */}
        <h2 className="mt-5 text-[26px] font-semibold leading-tight text-[#00424B] md:text-[22px] md:font-medium md:text-ink 3xl:text-[26px]">
          {/* An upgrade that went through is headed by what somebody now has
              rather than by the fact that a card was charged. They were on a
              term and pressed a button to be on a longer one; "Payment
              successful" answers a question they were not asking, and the
              term they ended up on is the one they were. Every other outcome
              keeps its own heading — a failed payment has not put anybody on
              anything. */}
          {paid && upgraded
            ? `You're on ${monthsLabel(months)}`
            : paid && scheduled
              ? "Downgrade scheduled"
              : title}
        </h2>

        {confirming ? (
          // The heading is the browser's account and is already on screen;
          // this line is the one thing still being checked.
          <div className="mt-3 flex flex-col items-center gap-2">
            {/* The line still being checked, as one block rather than two. */}
            <SkeletonCard className="h-[38px] w-[300px] max-w-full" />
            <span role="status" className="sr-only">
              Confirming your payment…
            </span>
          </div>
        ) : (
          <p className="mt-2 text-[15px] leading-[1.7] text-ink-muted max-md:mt-3 md:text-[14px] md:leading-relaxed 3xl:text-[15px]">
            {paid && !testMode && !upgraded && !scheduled && (
              <>
                You&rsquo;re now on{" "}
                <span className="font-medium text-ink">
                  {productName}
                </span>
                .{" "}
              </>
            )}
            {detail()}
          </p>
        )}

        {/* Drawn for every outcome that had a price attached to it. A failed
            payment still has to say what it was for and how much — that is
            what somebody checks against their bank statement before deciding
            whether they have in fact been charged twice. */}
        {outcome.state !== "unavailable" && (
          <div className="w-full">
            <TransactionDetails rows={rows} tone={tone} />
          </div>
        )}

        {/* The way onward, under the receipt. On a phone it is not here at
            all — it is the bar across the foot of the screen; see the end of
            this file. */}
        {!isMobile && (
          <div className="mt-7 flex w-full items-stretch gap-3 sm:justify-center">
            {ways}
          </div>
        )}

        {outcome.state === "pending" && (
          <p className="mt-5 text-[12px] 3xl:text-[13px] text-ink-subtle">
            You can close this page. Nothing is charged until the payment is
            confirmed.
          </p>
        )}
      </div>

      {/* The phone's way onward, across the foot of the screen — the same bar
          the price list, the checkout and the trial confirmation end in, so
          the button does not change place between paying and being told it
          worked. A child of the page rather than of the column above, because
          the blur runs the full width: the classes are this page's gutter
          cancelled so it can, put back inside so the button still lines up
          with the card, and the page's last padding taken off so the bar
          reaches the bottom of the screen.

          It is also what stands the app's tab bar down. A receipt is the end
          of a job rather than a place to be — there is one thing to do on it,
          and a row of tabs under that button offers five more. See
          `PinnedCtaBar`, which claims the bottom edge. */}
      {isMobile && (
        <PinnedCtaBar className="-mx-page px-page -mb-page">
          <div className="flex items-stretch gap-3">{ways}</div>
        </PinnedCtaBar>
      )}
    </div>
  );
};

export default SubscriptionResultPage;
